import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import NotificationsPanel from "./notifications/NotificationsPanel";
import { getUnreadCount } from "./notifications/NotificationService";
import {
  RiSunLine,
  RiHome3Line,
  RiCalendarCheckLine,
  RiTodoLine,
  RiRepeat2Line,
  RiFileTextLine,
  RiBookOpenLine,
  RiWallet3Line,
  RiCalendarLine,
  RiFolderLine,
  RiBriefcaseLine,
  RiBrainLine,
  RiBarChartLine,
  RiLayoutGridLine,
  RiSearchLine,
  RiShieldLine,
  RiLogoutBoxRLine,
  RiBellLine,
  RiMenuLine,
  RiAlertLine,
  RiFlashlightLine,
} from "react-icons/ri";
import "./Header.css";

const PRIMARY_NAV = [
  { label: "Today",     path: "/today",     Icon: RiSunLine },
  { label: "Dashboard", path: "/dashboard", Icon: RiHome3Line },
  { label: "Planner",   path: "/planner",   Icon: RiCalendarCheckLine },
  { label: "Tasks",     path: "/tasks",     Icon: RiTodoLine },
  { label: "Habits",    path: "/habits",    Icon: RiRepeat2Line },
];

const SECONDARY_NAV = [
  { label: "Notes",         path: "/notes",    Icon: RiFileTextLine },
  { label: "Journal",       path: "/journal",  Icon: RiBookOpenLine },
  { label: "Finance",       path: "/finance",  Icon: RiWallet3Line },
  { label: "Calendar",      path: "/calendar", Icon: RiCalendarLine },
  { label: "Files",         path: "/files",    Icon: RiFolderLine },
  { label: "Projects",      path: "/projects", Icon: RiBriefcaseLine },
  { label: "Topics",        path: "/topics",   Icon: RiBrainLine },
  { label: "Weekly Review", path: "/review",   Icon: RiBarChartLine },
  { label: "Spaces",        path: "/spaces",   Icon: RiLayoutGridLine },
  { label: "Search",        path: "/search",   Icon: RiSearchLine },
];

export default function Header({ pendingReviewCount = 0, holdReviewCount = 0, onReviewClick }) {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const displayName = auth?.username || "Guest";
  const isAdminRole = ["OWNER", "ADMIN", "SUPPORT", "READ_ONLY"].includes(auth?.role);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let timer;
    const load = async () => {
      if (!auth?.token) { setUnreadCount(0); return; }
      const count = await getUnreadCount();
      setUnreadCount(count);
    };
    load();
    timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [auth?.token]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (!auth?.token) return null;

  const go = (path) => { navigate(path); setMobileOpen(false); };
  const handleLogout = () => { logout(); navigate("/login"); };
  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const NavItem = ({ label, path, Icon }) => (
    <button
      className={`sidebar-nav-item${isActive(path) ? " active" : ""}`}
      type="button"
      onClick={() => go(path)}
    >
      <Icon className="sidebar-nav-icon" />
      <span className="sidebar-nav-label">{label}</span>
    </button>
  );

  return (
    <>
      <button
        className="sidebar-hamburger"
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu"
      >
        <RiMenuLine />
      </button>

      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`app-sidebar${mobileOpen ? " sidebar-open" : ""}`}>
        {/* Brand */}
        <div
          className="sidebar-brand"
          onClick={() => go("/today")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && go("/today")}
        >
          <div className="sidebar-brand-icon">L</div>
          <span className="sidebar-brand-name">LifeOS</span>
        </div>

        {/* Quick jump — opens the global command palette (also Ctrl/Cmd+K anywhere) */}
        <button
          className="sidebar-nav-item sidebar-quickjump"
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent("lifeos:open-command-palette"));
            setMobileOpen(false);
          }}
        >
          <RiFlashlightLine className="sidebar-nav-icon" />
          <span className="sidebar-nav-label">Quick Jump</span>
          <kbd className="sidebar-kbd">⌘K</kbd>
        </button>

        {/* Primary nav */}
        <nav className="sidebar-section">
          <div className="sidebar-section-label">Main</div>
          {PRIMARY_NAV.map((item) => <NavItem key={item.path} {...item} />)}
        </nav>

        <div className="sidebar-divider" />

        {/* Secondary nav */}
        <nav className="sidebar-section">
          <div className="sidebar-section-label">More</div>
          {SECONDARY_NAV.map((item) => <NavItem key={item.path} {...item} />)}
          {isAdminRole && (
            <NavItem label="Admin" path="/admin" Icon={RiShieldLine} />
          )}
        </nav>

        <div className="sidebar-spacer" />

        {/* Bottom section */}
        <div className="sidebar-bottom">
          {(pendingReviewCount > 0 || holdReviewCount > 0) && (
            <button
              className="sidebar-nav-item sidebar-review"
              type="button"
              onClick={() => onReviewClick?.()}
            >
              <RiAlertLine className="sidebar-nav-icon" />
              <span className="sidebar-nav-label">Review</span>
              <span className="sidebar-badge sidebar-badge-primary">{pendingReviewCount}</span>
              {holdReviewCount > 0 && (
                <span className="sidebar-badge sidebar-badge-warning">{holdReviewCount}</span>
              )}
            </button>
          )}

          <button
            className="sidebar-nav-item"
            type="button"
            onClick={() => setNotificationsOpen((p) => !p)}
          >
            <RiBellLine className="sidebar-nav-icon" />
            <span className="sidebar-nav-label">Notifications</span>
            {unreadCount > 0 && (
              <span className="sidebar-badge sidebar-badge-danger">{unreadCount}</span>
            )}
          </button>

          <div className="sidebar-divider" style={{ margin: "6px 0" }} />

          <div className="sidebar-user-row">
            <div className="sidebar-avatar">{displayName[0]?.toUpperCase()}</div>
            <span className="sidebar-user-name">{displayName}</span>
            <button
              className="sidebar-logout"
              type="button"
              onClick={handleLogout}
              title="Sign out"
            >
              <RiLogoutBoxRLine />
            </button>
          </div>
        </div>

        <NotificationsPanel
          open={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
        />
      </aside>
    </>
  );
}
